import { useEffect, useRef } from "react";

import { smpteToFrame } from "framerate-utils";

import { frameRateFromFio, normalizeTimecode } from "./playerUtils";

type RafVideoSyncParams = {
	enabled: boolean;

	primaryPlayerRef: any;
	followerPlayerRef: any;

	primaryVideoUrl?: string;
	followerVideoUrl?: string;

	primaryAssetInfo?: any;
	followerAssetInfo?: any;

	/**
	 * Enable console.debug logging for actual follower currentTime writes.
	 *
	 * You can also enable this at runtime with:
	 * localStorage.setItem("debugRafVideoSync", "1")
	 */
	debug?: boolean;
};

type SyncMode = "timecode" | "frame" | "normalizedTime" | "directTime";
type SyncRangeState = "inside" | "beforeFollowerRange" | "afterFollowerRange" | "unknown";

type SyncTarget = {
	targetTime: number;
	targetFrame: number | null;
	mode: SyncMode;
	rangeState: SyncRangeState;
	snapped?: boolean;
	sourceFrame?: number | null;
	rawFollowerFrame?: number | null;
};

type AssetTimecodeTiming = {
	fps: number;
	frames: number;
	startTimecode: string;
	startTimecodeFrame: number;
	frameRate: any;
};

type LastFollowerSet = {
	targetTime: number;
	targetFrame: number | null;
	mode: SyncMode;
	rangeState: SyncRangeState;
	setAtMs: number;
};

type ShouldUpdateResult = {
	shouldUpdate: boolean;
	reason: string;
	drift: number;
	followerFrame: number | null;
};

const DEBUG_LOCAL_STORAGE_KEY = "debugRafVideoSync";
const SAME_FRAME_RETRY_DELAY_MS = 200;

/**
 * Instead of snapping to whole frames, snap to quarter-frame positions.
 *
 * Example at 24fps:
 * frame positions become:
 * 0, 0.25, 0.5, 0.75, 1, 1.25, ...
 *
 * This keeps seeking quantized, but removes the jaggy full-frame jumps.
 */
const FRAME_SNAP_STEP = 0.25;
const FRAME_EPSILON = 0.0001;

function parsePositiveNumber(value: any): number {
	const parsed = Number(value);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function clamp(value: number, min: number, max: number) {
	return Math.min(Math.max(value, min), max);
}

function numbersAlmostEqual(a: number, b: number, threshold = 0.001) {
	return Math.abs(a - b) <= threshold;
}

function nowMs() {
	if (typeof performance !== "undefined" && typeof performance.now === "function") {
		return performance.now();
	}

	return Date.now();
}

function isDebugEnabled(debug?: boolean) {
	if (debug) {
		return true;
	}

	try {
		return typeof window !== "undefined" && window.localStorage?.getItem(DEBUG_LOCAL_STORAGE_KEY) === "1";
	} catch {
		return false;
	}
}

function debugRafVideoSync(debug: boolean | undefined, message: string, payload: Record<string, any>) {
	if (!isDebugEnabled(debug)) {
		return;
	}

	console.debug(`[useRafVideoSync] ${message}`, payload);
}

function getInternalVideo(playerRef: any): HTMLVideoElement | null {
	const player = playerRef?.current?.getInternalPlayer?.();

	if (!player) {
		return null;
	}

	return player as HTMLVideoElement;
}

function getAssetFrameDuration(assetInfo: any): number {
	const fps = parsePositiveNumber(assetInfo?.fps);
	const frames = parsePositiveNumber(assetInfo?.frames);

	if (fps <= 0 || frames <= 0) {
		return 0;
	}

	return frames / fps;
}

function getSafeDuration(video: HTMLVideoElement | null, assetInfo: any): number {
	const videoDuration = parsePositiveNumber(video?.duration);
	const assetDuration = parsePositiveNumber(assetInfo?.duration);
	const frameDuration = getAssetFrameDuration(assetInfo);

	return videoDuration || assetDuration || frameDuration || 0;
}

function getSafeFrameCount(video: HTMLVideoElement | null, assetInfo: any): number {
	const explicitFrames = parsePositiveNumber(assetInfo?.frames);

	if (explicitFrames > 0) {
		return Math.floor(explicitFrames);
	}

	const fps = parsePositiveNumber(assetInfo?.fps);
	const duration = getSafeDuration(video, assetInfo);

	if (fps > 0 && duration > 0) {
		return Math.floor(duration * fps);
	}

	return 0;
}

function getFollowerUpdateThreshold(followerAssetInfo: any) {
	const followerFps = parsePositiveNumber(followerAssetInfo?.fps);

	/**
	 * Because target positions now move in quarter-frame steps,
	 * tolerate up to one quarter-frame of drift before correcting.
	 */
	return followerFps > 0 ? FRAME_SNAP_STEP / followerFps : 0.025;
}

function snapFramePosition(framePosition: number): number {
	if (!Number.isFinite(framePosition)) {
		return 0;
	}

	return Math.max(0, Math.floor((framePosition + FRAME_EPSILON) / FRAME_SNAP_STEP) * FRAME_SNAP_STEP);
}

function timeToFramePosition(time: number, fps: number): number {
	if (!Number.isFinite(time) || fps <= 0) {
		return 0;
	}

	return Math.max(0, time * fps);
}

function timeToSnappedFramePosition(time: number, fps: number): number {
	return snapFramePosition(timeToFramePosition(time, fps));
}

function getMaxSnappedFramePosition(frameCount: number): number {
	if (frameCount <= 0) {
		return 0;
	}

	// Valid frame-time positions are [0, frameCount).
	// With quarter-frame snapping, the last valid snapped position is frameCount - 0.25.
	return Math.max(0, frameCount - FRAME_SNAP_STEP);
}

function clampFramePosition(framePosition: number, frameCount: number): number {
	const safeFramePosition = Number.isFinite(framePosition) ? framePosition : 0;

	if (frameCount <= 0) {
		return Math.max(0, safeFramePosition);
	}

	return clamp(safeFramePosition, 0, getMaxSnappedFramePosition(frameCount));
}

function getFrameRangeState(rawFrame: number, frameCount: number): SyncRangeState {
	if (!Number.isFinite(rawFrame) || frameCount <= 0) {
		return "unknown";
	}

	if (rawFrame < 0) {
		return "beforeFollowerRange";
	}

	if (rawFrame >= frameCount) {
		return "afterFollowerRange";
	}

	return "inside";
}

function getRawAssetStartTimecode(assetInfo: any): string | null {
	const rawTimecode = assetInfo?.metadata?.timecode ?? assetInfo?.timecode;

	if (typeof rawTimecode !== "string") {
		return null;
	}

	const trimmedTimecode = rawTimecode.trim();

	if (!trimmedTimecode.length) {
		return null;
	}

	return trimmedTimecode;
}

function getAssetStartTimecode(assetInfo: any): string | null {
	const rawTimecode = getRawAssetStartTimecode(assetInfo);

	if (!rawTimecode) {
		return null;
	}

	const normalizedTimecode = normalizeTimecode(rawTimecode);

	if (typeof normalizedTimecode !== "string" || !normalizedTimecode.trim().length) {
		return null;
	}

	return normalizedTimecode;
}

function getAssetTimecodeTiming(assetInfo: any): AssetTimecodeTiming | null {
	const fps = parsePositiveNumber(assetInfo?.fps);
	const frames = parsePositiveNumber(assetInfo?.frames);

	if (fps <= 0 || frames <= 0) {
		return null;
	}

	const frameRate = frameRateFromFio(assetInfo?.fps);

	if (typeof frameRate === "undefined") {
		return null;
	}

	const startTimecode = getAssetStartTimecode(assetInfo);

	// Important: 00:00:00:00 is a valid timecode.
	// Only missing/empty raw timecode is invalid.
	if (!startTimecode) {
		return null;
	}

	try {
		const startTimecodeFrame = smpteToFrame(frameRate, startTimecode);

		if (!Number.isFinite(startTimecodeFrame)) {
			return null;
		}

		return {
			fps,
			frames: Math.floor(frames),
			startTimecode,
			startTimecodeFrame,
			frameRate,
		};
	} catch {
		return null;
	}
}

function canUseFrameSync(primaryAssetInfo: any, followerAssetInfo: any) {
	const primaryFps = parsePositiveNumber(primaryAssetInfo?.fps);
	const followerFps = parsePositiveNumber(followerAssetInfo?.fps);

	const primaryFrames = parsePositiveNumber(primaryAssetInfo?.frames);
	const followerFrames = parsePositiveNumber(followerAssetInfo?.frames);

	return primaryFps > 0 && followerFps > 0 && primaryFrames > 0 && followerFrames > 0;
}

function getPrimaryCurrentFrame(primaryVideo: HTMLVideoElement, primaryFps: number, primaryFrames: number): number {
	const primaryFrame = timeToSnappedFramePosition(primaryVideo.currentTime, primaryFps);

	return clampFramePosition(primaryFrame, Math.floor(primaryFrames));
}

function getVideoCurrentFrameFromAsset(video: HTMLVideoElement, assetInfo: any): number | null {
	const fps = parsePositiveNumber(assetInfo?.fps);

	if (fps <= 0) {
		return null;
	}

	const frameCount = getSafeFrameCount(video, assetInfo);
	const frame = timeToSnappedFramePosition(video.currentTime, fps);

	return clampFramePosition(frame, frameCount);
}

function getTimecodeSyncTargetTime(primaryVideo: HTMLVideoElement, primaryAssetInfo: any, followerAssetInfo: any): SyncTarget | null {
	const primaryTiming = getAssetTimecodeTiming(primaryAssetInfo);
	const followerTiming = getAssetTimecodeTiming(followerAssetInfo);

	if (!primaryTiming || !followerTiming) {
		return null;
	}

	// Timecode frame matching is only safe when both assets use the same effective FPS.
	// If FPS differs, fall back to existing frame / normalized-time sync.
	if (!numbersAlmostEqual(primaryTiming.fps, followerTiming.fps)) {
		return null;
	}

	const primaryFrame = getPrimaryCurrentFrame(primaryVideo, primaryTiming.fps, primaryTiming.frames);
	const primaryAbsoluteTimecodeFrame = primaryTiming.startTimecodeFrame + primaryFrame;

	const rawFollowerFrame = primaryAbsoluteTimecodeFrame - followerTiming.startTimecodeFrame;
	const followerFrame = clampFramePosition(snapFramePosition(rawFollowerFrame), followerTiming.frames);

	return {
		targetTime: followerFrame / followerTiming.fps,
		targetFrame: followerFrame,
		mode: "timecode",
		rangeState: getFrameRangeState(rawFollowerFrame, followerTiming.frames),
		sourceFrame: primaryFrame,
		rawFollowerFrame,
	};
}

function getFrameSyncTargetTime(primaryVideo: HTMLVideoElement, primaryAssetInfo: any, followerAssetInfo: any): SyncTarget {
	const primaryFps = parsePositiveNumber(primaryAssetInfo?.fps);
	const followerFps = parsePositiveNumber(followerAssetInfo?.fps);

	const primaryFrames = Math.floor(parsePositiveNumber(primaryAssetInfo?.frames));
	const followerFrames = Math.floor(parsePositiveNumber(followerAssetInfo?.frames));

	const primaryFrame = getPrimaryCurrentFrame(primaryVideo, primaryFps, primaryFrames);
	const rawFollowerFrame = primaryFrame;
	const followerFrame = clampFramePosition(snapFramePosition(rawFollowerFrame), followerFrames);

	return {
		targetTime: followerFrame / followerFps,
		targetFrame: followerFrame,
		mode: "frame",
		rangeState: getFrameRangeState(rawFollowerFrame, followerFrames),
		sourceFrame: primaryFrame,
		rawFollowerFrame,
	};
}

function getNormalizedTimeTarget(primaryVideo: HTMLVideoElement, followerVideo: HTMLVideoElement, primaryAssetInfo: any, followerAssetInfo: any): SyncTarget {
	const primaryDuration = getSafeDuration(primaryVideo, primaryAssetInfo);
	const followerDuration = getSafeDuration(followerVideo, followerAssetInfo);

	if (primaryDuration > 0 && followerDuration > 0) {
		const normalizedProgress = clamp(primaryVideo.currentTime / primaryDuration, 0, 1);

		return {
			targetTime: normalizedProgress * followerDuration,
			targetFrame: null,
			mode: "normalizedTime",
			rangeState: "inside",
			sourceFrame: null,
			rawFollowerFrame: null,
		};
	}

	return {
		targetTime: primaryVideo.currentTime,
		targetFrame: null,
		mode: "directTime",
		rangeState: "unknown",
		sourceFrame: null,
		rawFollowerFrame: null,
	};
}

function getSyncTarget(primaryVideo: HTMLVideoElement, followerVideo: HTMLVideoElement, primaryAssetInfo: any, followerAssetInfo: any): SyncTarget {
	const timecodeSyncTarget = getTimecodeSyncTargetTime(primaryVideo, primaryAssetInfo, followerAssetInfo);

	if (timecodeSyncTarget) {
		return timecodeSyncTarget;
	}

	if (canUseFrameSync(primaryAssetInfo, followerAssetInfo)) {
		return getFrameSyncTargetTime(primaryVideo, primaryAssetInfo, followerAssetInfo);
	}

	return getNormalizedTimeTarget(primaryVideo, followerVideo, primaryAssetInfo, followerAssetInfo);
}

function snapSyncTargetToFollowerFrame(syncTarget: SyncTarget, followerVideo: HTMLVideoElement, followerAssetInfo: any): SyncTarget {
	const followerFps = parsePositiveNumber(followerAssetInfo?.fps);

	if (followerFps <= 0) {
		return syncTarget;
	}

	const followerFrameCount = getSafeFrameCount(followerVideo, followerAssetInfo);

	const rawFrame = syncTarget.targetFrame !== null ? syncTarget.targetFrame : timeToSnappedFramePosition(syncTarget.targetTime, followerFps);
	const snappedFrame = clampFramePosition(snapFramePosition(rawFrame), followerFrameCount);

	return {
		...syncTarget,
		targetFrame: snappedFrame,
		targetTime: snappedFrame / followerFps,
		snapped: true,
		rangeState: syncTarget.rangeState === "unknown" ? getFrameRangeState(rawFrame, followerFrameCount) : syncTarget.rangeState,
		rawFollowerFrame: syncTarget.rawFollowerFrame ?? rawFrame,
	};
}

function safeSetCurrentTime(video: HTMLVideoElement, targetTimeInput: number): number | null {
	if (!video || !Number.isFinite(targetTimeInput)) {
		return null;
	}

	const duration = parsePositiveNumber(video.duration);
	const maxTime = duration > 0 ? Math.max(0, duration - 0.001) : Number.MAX_SAFE_INTEGER;
	const targetTime = clamp(targetTimeInput, 0, maxTime);

	try {
		video.currentTime = targetTime;
		return targetTime;
	} catch {
		return null;
	}
}

function shouldUpdateFollower(video: HTMLVideoElement, syncTarget: SyncTarget, followerAssetInfo: any, lastSet: LastFollowerSet | null): ShouldUpdateResult {
	const threshold = getFollowerUpdateThreshold(followerAssetInfo);
	const drift = Math.abs(video.currentTime - syncTarget.targetTime);
	const followerFrame = getVideoCurrentFrameFromAsset(video, followerAssetInfo);
	const currentMs = nowMs();

	if (syncTarget.targetFrame !== null) {
		const sameRequestedFrame = lastSet?.targetFrame === syncTarget.targetFrame;
		const sameRequestedMode = lastSet?.mode === syncTarget.mode;
		const sameRequest = Boolean(sameRequestedFrame && sameRequestedMode);

		if (sameRequest) {
			const elapsedSinceSet = lastSet ? currentMs - lastSet.setAtMs : Number.MAX_SAFE_INTEGER;

			if (video.seeking && elapsedSinceSet <= SAME_FRAME_RETRY_DELAY_MS) {
				return {
					shouldUpdate: false,
					reason: "same-quarter-frame-seek-pending",
					drift,
					followerFrame,
				};
			}

			if (followerFrame === syncTarget.targetFrame && drift <= threshold) {
				return {
					shouldUpdate: false,
					reason: "same-quarter-frame",
					drift,
					followerFrame,
				};
			}

			if (drift > threshold && elapsedSinceSet > SAME_FRAME_RETRY_DELAY_MS) {
				return {
					shouldUpdate: true,
					reason: "retry-same-quarter-frame-after-drift",
					drift,
					followerFrame,
				};
			}

			return {
				shouldUpdate: false,
				reason: "same-quarter-frame-recently-requested",
				drift,
				followerFrame,
			};
		}

		if (video.seeking && lastSet && currentMs - lastSet.setAtMs <= SAME_FRAME_RETRY_DELAY_MS) {
			return {
				shouldUpdate: false,
				reason: "new-quarter-frame-waiting-for-previous-seek",
				drift,
				followerFrame,
			};
		}

		if (followerFrame === syncTarget.targetFrame && drift <= threshold) {
			return {
				shouldUpdate: false,
				reason: "already-on-quarter-frame",
				drift,
				followerFrame,
			};
		}

		if (drift <= threshold) {
			return {
				shouldUpdate: false,
				reason: "within-quarter-frame-threshold",
				drift,
				followerFrame,
			};
		}

		return {
			shouldUpdate: true,
			reason: "new-quarter-frame",
			drift,
			followerFrame,
		};
	}

	if (drift <= threshold) {
		return {
			shouldUpdate: false,
			reason: "within-quarter-frame-threshold",
			drift,
			followerFrame,
		};
	}

	if (lastSet && Math.abs(lastSet.targetTime - syncTarget.targetTime) <= threshold && currentMs - lastSet.setAtMs <= SAME_FRAME_RETRY_DELAY_MS) {
		return {
			shouldUpdate: false,
			reason: "same-time-recently-requested",
			drift,
			followerFrame,
		};
	}

	return {
		shouldUpdate: true,
		reason: "time-drift",
		drift,
		followerFrame,
	};
}

export function useRafVideoSync({ enabled, primaryPlayerRef, followerPlayerRef, primaryVideoUrl, followerVideoUrl, primaryAssetInfo, followerAssetInfo, debug }: RafVideoSyncParams) {
	const latestParamsRef = useRef({
		enabled,
		primaryPlayerRef,
		followerPlayerRef,
		primaryAssetInfo,
		followerAssetInfo,
		debug,
	});

	const lastFollowerSetRef = useRef<LastFollowerSet | null>(null);

	useEffect(() => {
		latestParamsRef.current = {
			enabled,
			primaryPlayerRef,
			followerPlayerRef,
			primaryAssetInfo,
			followerAssetInfo,
			debug,
		};
	}, [enabled, primaryPlayerRef, followerPlayerRef, primaryAssetInfo, followerAssetInfo, debug]);

	useEffect(() => {
		lastFollowerSetRef.current = null;
	}, [primaryVideoUrl, followerVideoUrl]);

	useEffect(() => {
		if (!enabled || !primaryVideoUrl || !followerVideoUrl) {
			return;
		}

		let rafId = 0;
		let disposed = false;

		const tick = () => {
			if (disposed) {
				return;
			}

			const latest = latestParamsRef.current;

			if (!latest.enabled) {
				rafId = requestAnimationFrame(tick);
				return;
			}

			const primaryVideo = getInternalVideo(latest.primaryPlayerRef);
			const followerVideo = getInternalVideo(latest.followerPlayerRef);

			if (!primaryVideo || !followerVideo) {
				rafId = requestAnimationFrame(tick);
				return;
			}

			if (!followerVideo.paused) {
				try {
					followerVideo.pause();
				} catch {}
			}

			if (primaryVideo.readyState < 1 || followerVideo.readyState < 1) {
				rafId = requestAnimationFrame(tick);
				return;
			}

			const rawSyncTarget = getSyncTarget(primaryVideo, followerVideo, latest.primaryAssetInfo, latest.followerAssetInfo);
			const syncTarget = snapSyncTargetToFollowerFrame(rawSyncTarget, followerVideo, latest.followerAssetInfo);
			const updateDecision = shouldUpdateFollower(followerVideo, syncTarget, latest.followerAssetInfo, lastFollowerSetRef.current);
			const updateThreshold = getFollowerUpdateThreshold(latest.followerAssetInfo);

			if (updateDecision.shouldUpdate) {
				const followerTimeBefore = followerVideo.currentTime;
				const appliedTime = safeSetCurrentTime(followerVideo, syncTarget.targetTime);

				if (appliedTime !== null) {
					lastFollowerSetRef.current = {
						targetTime: appliedTime,
						targetFrame: syncTarget.targetFrame,
						mode: syncTarget.mode,
						rangeState: syncTarget.rangeState,
						setAtMs: nowMs(),
					};

					debugRafVideoSync(latest.debug, "set follower currentTime", {
						reason: updateDecision.reason,
						mode: syncTarget.mode,
						rangeState: syncTarget.rangeState,
						snapped: Boolean(syncTarget.snapped),
						snapStep: FRAME_SNAP_STEP,

						sourceFrame: syncTarget.sourceFrame,
						rawFollowerFrame: syncTarget.rawFollowerFrame,
						targetFrame: syncTarget.targetFrame,

						targetTime: appliedTime,
						rawTargetTime: rawSyncTarget.targetTime,

						primaryTime: primaryVideo.currentTime,
						followerTimeBefore,
						followerTimeAfter: followerVideo.currentTime,

						followerFrameBefore: updateDecision.followerFrame,
						drift: updateDecision.drift,
						threshold: updateThreshold,
						followerSeeking: followerVideo.seeking,

						primaryFps: latest.primaryAssetInfo?.fps,
						followerFps: latest.followerAssetInfo?.fps,
						primaryFrames: latest.primaryAssetInfo?.frames,
						followerFrames: latest.followerAssetInfo?.frames,
					});
				}
			}

			rafId = requestAnimationFrame(tick);
		};

		rafId = requestAnimationFrame(tick);

		return () => {
			disposed = true;
			cancelAnimationFrame(rafId);
		};
	}, [enabled, primaryVideoUrl, followerVideoUrl]);
}
